import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Switch,
  TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useToast } from '../../context/ToastContext';
import { helpWhy } from '../../help/why';
import { AuthService } from '../../services/auth.service';
import { ApprovalWorkflowService } from '../../services/approval.service';
import { SearchableSelect } from '../common/SearchableSelect';
import {
  APPROVER_TYPE_LABEL, ApprovalOption, ApprovalStepRequest, ApprovalSubjectType, ApprovalWorkflow, ApproverOptions, ApproverType,
} from '../../interfaces/approval.interface';
import { MATCH_MODE_LABEL, OPERATOR_LABEL, RuleField, RuleMatchMode, RuleOperator } from '../../interfaces/bank/bankRule.interface';

interface CondRow { field: string; operator: RuleOperator | ''; valueText: string; valueNumber: string; valueNumberTo: string }
interface StepRow { approverType: ApproverType; approverUserId: string; approverRoleId: string; departmentId: string; authorityLimit: string }

const emptyStep = (): StepRow => ({ approverType: 'ROLE', approverUserId: '', approverRoleId: '', departmentId: '', authorityLimit: '' });

/** Picker of one REFERENCE condition. Customers are searched on the server (never a first-200 slice); the small departments
 *  list comes from the approver options. */
const ReferenceValuePicker: React.FC<{
  institutionId: string; subjectType: string; field: string; value: string; local: ApprovalOption[] | null;
  onChange: (id: string) => void;
}> = ({ institutionId, subjectType, field, value, local, onChange }) => {
  const [options, setOptions] = useState<ApprovalOption[]>(local ?? []);
  const [selected, setSelected] = useState<ApprovalOption | null>(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = (text: string) => {
    if (local) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setLoading(true);
      Promise.resolve(ApprovalWorkflowService.referenceOptions(institutionId, subjectType, field, text))
        .then(o => setOptions(o ?? [])).catch(() => setOptions([])).finally(() => setLoading(false));
    }, 250);
  };
  useEffect(() => {
    if (local) { setOptions(local); return; }
    search('');
    if (value) {
      Promise.resolve(ApprovalWorkflowService.referenceOptions(institutionId, subjectType, field, undefined, value))
        .then(o => setSelected(o?.[0] ?? null)).catch(() => undefined);
    }
    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local, field]);

  return (
    <SearchableSelect<ApprovalOption> options={options} value={value} data-help="approvals.editor.field.conditionValue" size="small" label="Value" loading={loading}
      selectedOption={selected} disableClientFilter={!local} onInputChange={search}
      onChange={(id, opt) => { setSelected(opt); onChange(id); }} getOptionLabel={o => o.name} />
  );
};

interface Props {
  subject: ApprovalSubjectType;
  workflow?: ApprovalWorkflow | null;
  options: ApproverOptions;
  /** id/name lists for REFERENCE condition fields, keyed by field code (departments come from {@code options}). */
  referenceOptions: Record<string, ApprovalOption[]>;
  onClose: () => void;
  onSaved: () => void;
}

/** Create / edit one approval workflow: conditions that pick it, up to five sequential steps, and the two switches. */
const ApprovalWorkflowEditorDialog: React.FC<Props> = ({ subject, workflow, options, referenceOptions, onClose, onSaved }) => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const seed = workflow ?? null;
  const fields = subject.kind.fields;
  const fieldOf = (code: string): RuleField | undefined => fields.find(f => f.code === code);
  const operatorsFor = (code: string): RuleOperator[] => {
    const f = fieldOf(code);
    if (!f) return [];
    return subject.kind.operators[f.type] ?? (f.type === 'REFERENCE' ? ['IS', 'IS_NOT'] : []);
  };
  // departments are a short list loaded with the approver options; everything else (customers) is searched on the server
  const localReferenceOptions = (code: string): ApprovalOption[] | null =>
    referenceOptions[code] ?? (code.includes('DEPARTMENT') ? options.departments : null);

  const [name, setName] = useState(seed?.rule.name ?? '');
  const [matchMode, setMatchMode] = useState<RuleMatchMode>(seed?.rule.matchMode ?? 'ALL');
  const [distinct, setDistinct] = useState(seed?.distinctApprovers ?? true);
  const [skipSubmitter, setSkipSubmitter] = useState(seed?.skipSubmitterSteps ?? false);
  const [conds, setConds] = useState<CondRow[]>(() => (seed?.rule.conditions ?? []).map(c => ({
    field: c.field, operator: c.operator, valueText: c.valueText ?? '',
    valueNumber: c.valueNumber != null ? String(c.valueNumber) : '', valueNumberTo: c.valueNumberTo != null ? String(c.valueNumberTo) : '',
  })));
  const [steps, setSteps] = useState<StepRow[]>(() => seed?.steps.length ? seed.steps.map(s => ({
    approverType: s.approverType, approverUserId: s.approverUserId ?? '', approverRoleId: s.approverRoleId ?? '',
    departmentId: s.departmentId ?? '', authorityLimit: s.authorityLimit != null ? String(s.authorityLimit) : '',
  })) : [emptyStep()]);
  const [saving, setSaving] = useState(false);

  const setCond = (i: number, patch: Partial<CondRow>) => setConds(rows => rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const setStep = (i: number, patch: Partial<StepRow>) => setSteps(rows => rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const condOk = (r: CondRow) => {
    const f = fieldOf(r.field);
    if (!f || !r.operator) return false;
    if (f.type === 'NUMBER') return r.valueNumber !== '' && (r.operator !== 'BETWEEN' || r.valueNumberTo !== '');
    return r.valueText.trim() !== '';
  };
  const stepOk = (s: StepRow) =>
    (s.approverType === 'USER' ? !!s.approverUserId : s.approverType === 'ROLE' ? !!s.approverRoleId
      : s.approverType === 'DEPARTMENT_ADMIN' ? !!s.departmentId : true);
  const valid = name.trim() !== '' && conds.every(condOk) && steps.every(stepOk);

  const save = async () => {
    const stepsReq: ApprovalStepRequest[] = steps.map(s => ({
      approverType: s.approverType,
      approverUserId: s.approverType === 'USER' ? s.approverUserId : null,
      approverRoleId: s.approverType === 'ROLE' ? s.approverRoleId : null,
      departmentId: s.approverType === 'DEPARTMENT_ADMIN' ? s.departmentId : null,
      authorityLimit: s.authorityLimit !== '' ? Number(s.authorityLimit) : null,
    }));
    const dto = {
      name: name.trim(), subjectType: subject.subjectType, matchMode, distinctApprovers: distinct, skipSubmitterSteps: skipSubmitter,
      conditions: conds.map(r => {
        const f = fieldOf(r.field);
        const num = f?.type === 'NUMBER';
        return {
          field: r.field, operator: r.operator as RuleOperator,
          valueText: num ? null : r.valueText.trim(),
          valueNumber: num && r.valueNumber !== '' ? Number(r.valueNumber) : null,
          valueNumberTo: num && r.operator === 'BETWEEN' && r.valueNumberTo !== '' ? Number(r.valueNumberTo) : null,
        };
      }),
      steps: stepsReq,
    };
    try {
      setSaving(true);
      if (seed) await ApprovalWorkflowService.update(institutionId, seed.rule.id, dto);
      else await ApprovalWorkflowService.create(institutionId, dto);
      toast.showSuccess(seed ? 'Workflow updated' : 'Workflow created');
      onSaved();
    } catch (e) {
      toast.showApiError(e, 'Failed to save the workflow');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} PaperProps={{ 'data-help-page': 'approvals.editor.page' } as any} maxWidth="md" fullWidth>
      <DialogTitle>{seed ? 'Edit approval workflow' : 'New approval workflow'} — {subject.label}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Box data-help-section="approvals.editor.section.basics" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pt: 1 }}>
          <TextField data-help="approvals.editor.field.name" label="Workflow name" required size="small" value={name} onChange={e => setName(e.target.value)}
            inputProps={{ maxLength: 120 }} sx={{ flex: '2 1 260px', maxWidth: 420 }} />
          <TextField select data-help="approvals.editor.field.match" label="Match" size="small" value={matchMode} onChange={e => setMatchMode(e.target.value as RuleMatchMode)}
            sx={{ flex: '1 1 220px', maxWidth: 300 }}>
            {(Object.keys(MATCH_MODE_LABEL) as RuleMatchMode[]).map(m => <MenuItem key={m} value={m}>{MATCH_MODE_LABEL[m]}</MenuItem>)}
          </TextField>
        </Box>

        <Typography variant="subtitle2">When (no conditions = every document of this type)</Typography>
        {conds.map((row, i) => {
          const f = fieldOf(row.field);
          return (
            <Box key={i} data-help-section="approvals.editor.section.condition" data-testid={`wf-condition-${i}`} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              <TextField select size="small" data-help="approvals.editor.field.conditionField" label="Field" value={row.field}
                onChange={e => setCond(i, { field: e.target.value, operator: operatorsFor(e.target.value)[0] ?? '', valueText: '', valueNumber: '', valueNumberTo: '' })}
                sx={{ flex: '1 1 150px', maxWidth: 200 }}>
                {fields.map(fl => <MenuItem key={fl.code} value={fl.code}>{fl.label}</MenuItem>)}
              </TextField>
              <TextField select size="small" data-help="approvals.editor.field.conditionOperator" label="Operator" value={row.operator} onChange={e => setCond(i, { operator: e.target.value as RuleOperator })}
                sx={{ flex: '1 1 160px', maxWidth: 210 }}>
                {operatorsFor(row.field).map(o => <MenuItem key={o} value={o}>{OPERATOR_LABEL[o]}</MenuItem>)}
              </TextField>
              {f?.type === 'NUMBER' ? (
                <>
                  <TextField size="small" type="number" data-help="approvals.editor.field.conditionAmount" label={row.operator === 'BETWEEN' ? 'From' : 'Amount'} value={row.valueNumber}
                    onChange={e => setCond(i, { valueNumber: e.target.value })} sx={{ flex: '1 1 140px', maxWidth: 200 }} />
                  {row.operator === 'BETWEEN' && (
                    <TextField size="small" type="number" data-help="approvals.editor.field.conditionTo" label="To" value={row.valueNumberTo}
                      onChange={e => setCond(i, { valueNumberTo: e.target.value })} sx={{ flex: '1 1 140px', maxWidth: 200 }} />
                  )}
                </>
              ) : f?.type === 'REFERENCE' ? (
                <Box sx={{ flex: '2 1 220px', maxWidth: 360 }}>
                  <ReferenceValuePicker institutionId={institutionId} subjectType={subject.subjectType} field={row.field}
                    value={row.valueText} local={localReferenceOptions(row.field)} onChange={id => setCond(i, { valueText: id })} />
                </Box>
              ) : f?.type === 'ENUM' ? (
                <TextField select size="small" data-help="approvals.editor.field.conditionValue" label="Value" value={row.valueText} onChange={e => setCond(i, { valueText: e.target.value })}
                  sx={{ flex: '1 1 150px', maxWidth: 220 }}>
                  {f.enumOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
              ) : (
                <TextField size="small" data-help="approvals.editor.field.conditionText" label="Text" value={row.valueText} onChange={e => setCond(i, { valueText: e.target.value })}
                  inputProps={{ maxLength: 255 }} sx={{ flex: '2 1 200px', maxWidth: 360 }} />
              )}
              <IconButton size="small" data-help="approvals.editor.removeCondition" aria-label="Remove condition" onClick={() => setConds(rows => rows.filter((_, idx) => idx !== i))}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
        <Box>
          <Button size="small" startIcon={<AddIcon />} data-help="approvals.editor.addCondition" disabled={conds.length >= subject.kind.maxConditions || fields.length === 0}
            onClick={() => setConds(rows => [...rows, { field: fields[0].code, operator: operatorsFor(fields[0].code)[0] ?? '', valueText: '', valueNumber: '', valueNumberTo: '' }])}>
            Add condition
          </Button>
        </Box>

        <Typography variant="subtitle2">Approval steps (in order, 1 to 5)</Typography>
        {steps.map((s, i) => (
          <Box key={i} data-help-section="approvals.editor.section.step" data-testid={`wf-step-${i}`} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            <Typography variant="body2" sx={{ width: 20 }}>{i + 1}.</Typography>
            <TextField select size="small" data-help="approvals.editor.field.approverType" label="Approver" value={s.approverType}
              onChange={e => setStep(i, { approverType: e.target.value as ApproverType })} sx={{ flex: '1 1 220px', maxWidth: 300 }}>
              {(Object.keys(APPROVER_TYPE_LABEL) as ApproverType[]).map(t => <MenuItem key={t} value={t}>{APPROVER_TYPE_LABEL[t]}</MenuItem>)}
            </TextField>
            {s.approverType === 'USER' && (
              <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
                <SearchableSelect<ApprovalOption> options={options.members} value={s.approverUserId} size="small"
                  onChange={id => setStep(i, { approverUserId: id })} getOptionLabel={o => o.name} data-help="approvals.editor.field.approverPerson" label="Person" />
              </Box>
            )}
            {s.approverType === 'ROLE' && (
              <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
                <SearchableSelect<ApprovalOption> options={options.roles} value={s.approverRoleId} size="small"
                  onChange={id => setStep(i, { approverRoleId: id })} getOptionLabel={o => o.name} data-help="approvals.editor.field.approverRole" label="Role" />
              </Box>
            )}
            {s.approverType === 'DEPARTMENT_ADMIN' && (
              <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
                <SearchableSelect<ApprovalOption> options={options.departments} value={s.departmentId} size="small"
                  onChange={id => setStep(i, { departmentId: id })} getOptionLabel={o => o.name} data-help="approvals.editor.field.approverDepartment" label="Department" />
              </Box>
            )}
            <TextField size="small" type="number" data-help="approvals.editor.field.authorityLimit" label="Authority limit (optional)" value={s.authorityLimit}
              onChange={e => setStep(i, { authorityLimit: e.target.value })} sx={{ flex: '1 1 170px', maxWidth: 220 }} />
            <IconButton size="small" data-help="approvals.editor.removeStep" aria-label="Remove step" disabled={steps.length <= 1}
              onClick={() => setSteps(rows => rows.filter((_, idx) => idx !== i))}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Box>
          <Button size="small" startIcon={<AddIcon />} data-help="approvals.editor.addStep" disabled={steps.length >= 5} onClick={() => setSteps(rows => [...rows, emptyStep()])}>Add step</Button>
        </Box>
        <Alert severity="info" sx={{ py: 0 }}>
          The person who submits a document can never approve it. A step with an authority limit ends the chain when the amount is within it.
        </Alert>
        <Box data-help-section="approvals.editor.section.switches" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <FormControlLabel control={<Switch checked={distinct} onChange={e => setDistinct(e.target.checked)} />}
            data-help="approvals.editor.field.distinct" label="Same person can not approve two steps" />
          <FormControlLabel control={<Switch checked={skipSubmitter} onChange={e => setSkipSubmitter(e.target.checked)} />}
            data-help="approvals.editor.field.skipSubmitter" label="Skip steps the submitter would have held" />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button data-help="approvals.editor.cancel" onClick={onClose}>Cancel</Button>
        <Button variant="contained" data-help="approvals.editor.save" disabled={saving || !valid} {...helpWhy(saving ? { kind: 'status', message: 'The workflow is being saved.' } : !valid ? { kind: 'status', message: 'Fill in the workflow name, every condition value and every approver first.' } : null)} onClick={save}>Save workflow</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalWorkflowEditorDialog;
